import { useMutation, useQuery, useQueryClient, type QueryKey } from "@tanstack/react-query";
import { ApiError, get, patch, post, put, qs } from "./client";
import type {
  AdminQueue,
  AuditEntry,
  Category,
  Choice,
  HistoryPoint,
  LedgerEntry,
  Leaderboard,
  Market,
  MarketDetail,
  MarketStatus,
  Me,
  Notification,
  Profile,
  Side,
  Trade,
  TradeResult,
  User,
  Vote,
} from "./types";

// Live prices are polled until the WebSocket push lands in phase 6.
export const LIVE_POLL_MS = 5000;

export const keys = {
  me: ["me"] as const,
  categories: ["categories"] as const,
  markets: (status: string, category: string) => ["markets", status, category] as const,
  market: (id: string) => ["market", id] as const,
  history: (id: string) => ["market", id, "history"] as const,
  trades: (id: string) => ["market", id, "trades"] as const,
  profile: (u: string) => ["profile", u] as const,
  leaderboard: (s: string) => ["leaderboard", s] as const,
  notifications: ["notifications"] as const,
  ledger: ["ledger"] as const,
  adminQueue: ["admin", "queue"] as const,
  adminUsers: (s: string) => ["admin", "users", s] as const,
  adminAudit: ["admin", "audit"] as const,
};

/** The signed-in user. A 401 resolves to null instead of an error. */
export function useMe() {
  return useQuery({
    queryKey: keys.me,
    queryFn: async () => {
      try {
        return await get<Me>("/me");
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 30_000,
  });
}

export function useCategories() {
  return useQuery({
    queryKey: keys.categories,
    queryFn: async () => (await get<{ categories: Category[] }>("/categories")).categories,
    staleTime: 5 * 60_000,
  });
}

export function useMarkets(status: MarketStatus, category: string) {
  return useQuery({
    queryKey: keys.markets(status, category),
    queryFn: async () => (await get<{ markets: Market[] }>("/markets" + qs({ status, category }))).markets,
    refetchInterval: status === "OPEN" ? LIVE_POLL_MS * 2 : false,
  });
}

export function useMarket(id: string) {
  return useQuery({
    queryKey: keys.market(id),
    queryFn: () => get<MarketDetail>(`/markets/${id}`),
    refetchInterval: (q) => (q.state.data?.market.status === "OPEN" || q.state.data?.market.status === "VOTING" ? LIVE_POLL_MS : false),
  });
}

export function useHistory(id: string, live: boolean) {
  return useQuery({
    queryKey: keys.history(id),
    queryFn: async () => (await get<{ points: HistoryPoint[] }>(`/markets/${id}/history`)).points,
    refetchInterval: live ? LIVE_POLL_MS : false,
  });
}

export function useTrades(id: string, live: boolean) {
  return useQuery({
    queryKey: keys.trades(id),
    queryFn: async () => (await get<{ trades: Trade[] }>(`/markets/${id}/trades?limit=30`)).trades,
    refetchInterval: live ? LIVE_POLL_MS : false,
  });
}

export function useProfile(username: string) {
  return useQuery({ queryKey: keys.profile(username), queryFn: () => get<Profile>(`/users/${encodeURIComponent(username)}`) });
}

export function useLeaderboard(season: string) {
  return useQuery({ queryKey: keys.leaderboard(season), queryFn: () => get<Leaderboard>("/leaderboard" + qs({ season })) });
}

export function useNotifications() {
  return useQuery({
    queryKey: keys.notifications,
    queryFn: async () => (await get<{ notifications: Notification[] }>("/me/notifications")).notifications,
  });
}

export function useLedger() {
  return useQuery({ queryKey: keys.ledger, queryFn: async () => (await get<{ entries: LedgerEntry[] }>("/me/ledger")).entries });
}

export function useAdminQueue(enabled: boolean) {
  return useQuery({ queryKey: keys.adminQueue, queryFn: () => get<AdminQueue>("/admin/queue"), enabled, refetchInterval: 60_000 });
}

export function useAdminUsers(status: string) {
  return useQuery({ queryKey: keys.adminUsers(status), queryFn: async () => (await get<{ users: User[] }>("/admin/users" + qs({ status }))).users });
}

export function useAudit() {
  return useQuery({ queryKey: keys.adminAudit, queryFn: async () => (await get<{ entries: AuditEntry[] }>("/admin/audit")).entries });
}

// ---- mutations -----------------------------------------------------------------

/** A mutation that refreshes the given queries when it succeeds. */
function useAction<TVars, TResult>(fn: (v: TVars) => Promise<TResult>, invalidate: (v: TVars) => QueryKey[]) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (_r, v) => Promise.all(invalidate(v).map((k) => qc.invalidateQueries({ queryKey: k }))),
  });
}

export interface TradeInput {
  side: Side;
  outcome: number;
  spendM?: number;
  sharesM?: number;
  minSharesM?: number;
  minProceedsM?: number;
}

export const quote = (marketId: string, input: TradeInput) => post<TradeResult>(`/markets/${marketId}/quote`, input);

export function useTrade(marketId: string) {
  return useAction(
    (input: TradeInput) => post<TradeResult>(`/markets/${marketId}/trade`, input),
    () => [keys.market(marketId), keys.history(marketId), keys.trades(marketId), keys.me, ["markets"]],
  );
}

export function useVote(marketId: string) {
  return useAction(
    (v: { choice: Choice; decidedEarly?: boolean; note?: string }) => put<{ vote: Vote }>(`/markets/${marketId}/vote`, v),
    () => [keys.market(marketId), keys.me],
  );
}

export function useReport(marketId: string) {
  return useAction((v: { reason: string; text?: string }) => post(`/markets/${marketId}/report`, v), () => []);
}

export interface NewMarketInput {
  type: "BINARY" | "MULTI";
  category: string;
  title: string;
  description: string;
  criteria: string;
  closeAt: string;
  eventAt?: string;
  liquidityM: number;
  outcomes?: string[];
}

export function useCreateMarket() {
  return useAction((v: NewMarketInput) => post<{ market: Market }>("/markets", v), () => [keys.me, ["markets"]]);
}

export function useEditMarket(id: string) {
  return useAction(
    (v: Partial<Pick<NewMarketInput, "title" | "description" | "criteria" | "closeAt" | "eventAt">>) => patch<{ market: Market }>(`/markets/${id}`, v),
    () => [keys.market(id)],
  );
}

export function useAdminMarketAction(id: string) {
  return useAction(
    (v: { action: "close" | "remove" | "resolve" | "reverse"; body?: unknown }) => post<{ market: Market }>(`/admin/markets/${id}/${v.action}`, v.body),
    () => [keys.market(id), keys.history(id), keys.trades(id), keys.adminQueue, ["markets"]],
  );
}

export function useAdminUserAction() {
  return useAction(
    (v: { id: string; action: "approve" | "ban" | "grant" | "reset-link"; body?: unknown }) =>
      post<{ user?: User; url?: string; expiresAt?: string }>(`/admin/users/${v.id}/${v.action}`, v.body),
    () => [["admin"]],
  );
}

export function useAdminDismiss() {
  return useAction(
    (v: { kind: "reports" | "flags"; marketId: string; id: string }) => post(`/admin/${v.kind}/${v.marketId}/${v.id}/dismiss`),
    () => [keys.adminQueue],
  );
}

export function useCreateInvite() {
  return useAction((v: { maxUses: number }) => post<{ code: string; url: string; maxUses: number; expiresAt: string }>("/admin/invites", v), () => [keys.adminAudit]);
}

export function useSaveCategories() {
  return useAction((cats: Category[]) => put<{ categories: Category[] }>("/admin/categories", cats), () => [keys.categories]);
}

export function useUpdateMe() {
  return useAction(
    (v: { displayName?: string; currentPassword?: string; newPassword?: string }) => patch<{ user: User }>("/me", v),
    () => [keys.me],
  );
}

export function useMarkNotificationsRead() {
  return useAction(() => post("/me/notifications/read"), () => [keys.me, keys.notifications]);
}
